import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { MdWarning, MdRefresh, MdCheckCircle, MdAccessTime, MdInfo, MdEmail, MdDeleteForever, MdSecurity, MdPhoneAndroid, MdKey, MdClose, MdCopyAll, MdLock } from 'react-icons/md';

function AccountSettingsPage() {
  const [resetStatus, setResetStatus] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMfaSetupDialog, setShowMfaSetupDialog] = useState(false);
  const [showMfaDisableDialog, setShowMfaDisableDialog] = useState(false);
  const [showRecoveryCodesDialog, setShowRecoveryCodesDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [prefsSuccess, setPrefsSuccess] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load reset status, preferences, and MFA status in parallel
      const [resetResponse, prefsResponse, mfaResponse] = await Promise.all([
        axios.get(`${API_URL}/api/account/reset-poll-profile/status`),
        axios.get(`${API_URL}/api/account/preferences`),
        axios.get(`${API_URL}/api/mfa/status`).catch(() => ({ data: { mfaEnabled: false } }))
      ]);
      
      setResetStatus(resetResponse.data);
      setPreferences(prefsResponse.data);
      setMfaStatus(mfaResponse.data);
    } catch (err) {
      console.error('Failed to load account settings:', err);
      setError(err.response?.data?.detail || 'Failed to load account settings');
    } finally {
      setLoading(false);
    }
  };

  // MFA Functions
  const handleSetupMfa = async () => {
    try {
      setMfaLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/mfa/setup`);
      setMfaSetupData(response.data);
      setRecoveryCodes(response.data.recoveryCodes);
      setShowMfaSetupDialog(true);
    } catch (err) {
      console.error('Failed to setup MFA:', err);
      setError(err.response?.data?.detail || 'Failed to setup MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMfaSetup = async () => {
    try {
      setMfaLoading(true);
      setError(null);
      await axios.post(`${API_URL}/api/mfa/verify-setup`, {
        otp_code: mfaVerifyCode
      });
      setSuccess('MFA has been successfully enabled!');
      setShowMfaSetupDialog(false);
      setShowRecoveryCodesDialog(true);
      setMfaVerifyCode('');
      await loadData();
    } catch (err) {
      console.error('Failed to verify MFA:', err);
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    try {
      setMfaLoading(true);
      setError(null);
      await axios.post(`${API_URL}/api/mfa/disable`, {
        otp_code: mfaDisableCode,
        password: mfaDisablePassword
      });
      setSuccess('MFA has been disabled.');
      setShowMfaDisableDialog(false);
      setMfaDisableCode('');
      setMfaDisablePassword('');
      await loadData();
    } catch (err) {
      console.error('Failed to disable MFA:', err);
      setError(err.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    const code = prompt('Enter your current 6-digit authenticator code to regenerate recovery codes:');
    if (!code) return;
    
    try {
      setMfaLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/mfa/regenerate-recovery-codes`, {
        otp_code: code
      });
      setRecoveryCodes(response.data.recoveryCodes);
      setShowRecoveryCodesDialog(true);
      setSuccess('New recovery codes generated!');
    } catch (err) {
      console.error('Failed to regenerate codes:', err);
      setError(err.response?.data?.detail || 'Failed to regenerate recovery codes');
    } finally {
      setMfaLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setSuccess('Recovery codes copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setPasswordSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      setPasswordSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    try {
      setSavingPrefs(true);
      setPrefsSuccess(null);
      
      // Update locally immediately for responsive UI
      setPreferences(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [key]: value
        }
      }));
      
      // Map frontend key to backend key
      const backendKeyMap = {
        'emailNotifyPollApproved': 'email_notify_poll_approved',
        'emailNotifyPollRejected': 'email_notify_poll_rejected',
        'emailNotifyPollFlagged': 'email_notify_poll_flagged',
        'emailNotifyPollClosed': 'email_notify_poll_closed'
      };
      
      await axios.put(`${API_URL}/api/account/preferences`, {
        [backendKeyMap[key]]: value
      });
      
      setPrefsSuccess('Preference saved');
      setTimeout(() => setPrefsSuccess(null), 2000);
    } catch (err) {
      console.error('Failed to update preference:', err);
      // Revert on error
      setPreferences(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [key]: !value
        }
      }));
      setError(err.response?.data?.detail || 'Failed to update preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'RESET') {
      setError('Please type RESET to confirm');
      return;
    }

    try {
      setResetting(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/account/reset-poll-profile`, {
        confirm: true
      });
      setSuccess(response.data.message);
      setShowConfirmDialog(false);
      setConfirmText('');
      await loadData();
    } catch (err) {
      console.error('Failed to reset poll profile:', err);
      setError(err.response?.data?.detail || 'Failed to reset poll profile');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      setDeleting(true);
      setError(null);
      await axios.delete(`${API_URL}/api/account/delete`, {
        data: {
          reason: deleteReason || null,
          confirm: true
        }
      });
      // Logout and redirect to home
      logout();
      navigate('/');
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(err.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-6">Account Settings</h1>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <MdCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-green-800 font-medium">{success}</p>
            <p className="text-green-600 text-sm mt-1">You can now re-vote on all polls.</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <MdWarning className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Email Notification Preferences Card */}
      <div className="bg-white rounded-lg shadow-card border border-gray-200 overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MdEmail className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 font-rubik">Email Notifications</h2>
              <p className="text-sm text-gray-500 font-rubik">Manage email notifications for your custom polls</p>
            </div>
            {prefsSuccess && (
              <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                <MdCheckCircle className="w-4 h-4" />
                {prefsSuccess}
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Email Status Notice */}
          {preferences && !preferences.emailVerified && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <MdWarning className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {preferences.email 
                  ? 'Your email is not verified. Please verify your email to receive notifications.'
                  : 'Add and verify an email address in your Profile to receive notifications.'
                }
              </p>
            </div>
          )}

          {/* Notification Toggles */}
          <div className="space-y-4">
            <NotificationToggle
              label="Poll Approved"
              description="Get notified when your custom poll is approved and goes live"
              enabled={preferences?.preferences?.emailNotifyPollApproved ?? true}
              onChange={(value) => handlePreferenceChange('emailNotifyPollApproved', value)}
              disabled={savingPrefs || !preferences?.emailVerified}
            />
            <NotificationToggle
              label="Poll Rejected"
              description="Get notified when your custom poll is not approved"
              enabled={preferences?.preferences?.emailNotifyPollRejected ?? true}
              onChange={(value) => handlePreferenceChange('emailNotifyPollRejected', value)}
              disabled={savingPrefs || !preferences?.emailVerified}
            />
            <NotificationToggle
              label="Poll Flagged"
              description="Get notified when your custom poll receives a flag/report"
              enabled={preferences?.preferences?.emailNotifyPollFlagged ?? true}
              onChange={(value) => handlePreferenceChange('emailNotifyPollFlagged', value)}
              disabled={savingPrefs || !preferences?.emailVerified}
            />
            <NotificationToggle
              label="Poll Closed"
              description="Get notified when your custom poll expires or is closed"
              enabled={preferences?.preferences?.emailNotifyPollClosed ?? true}
              onChange={(value) => handlePreferenceChange('emailNotifyPollClosed', value)}
              disabled={savingPrefs || !preferences?.emailVerified}
            />
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication Card */}
      <div className="bg-white rounded-lg shadow-card border border-gray-200 overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MdSecurity className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 font-rubik">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-500 font-rubik">Add an extra layer of security to your account</p>
            </div>
            {mfaStatus?.mfaEnabled && (
              <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <MdCheckCircle className="w-3 h-3" />
                Enabled
              </span>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {mfaStatus?.schemaMigrationNeeded ? (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <MdInfo className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">Setup Required</p>
                <p className="mt-1">
                  The database needs to be updated to enable MFA. Please contact your administrator 
                  to run the schema migration: <code className="bg-blue-100 px-1 rounded">/app/docs/mfa_schema.sql</code>
                </p>
              </div>
            </div>
          ) : !mfaStatus?.mfaEnabled ? (
            <>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <MdPhoneAndroid className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700 font-medium">How it works</p>
                    <ul className="text-sm text-gray-600 mt-2 space-y-1 list-disc list-inside">
                      <li>Download Google Authenticator on your phone</li>
                      <li>Scan a QR code to link your account</li>
                      <li>Enter a 6-digit code each time you log in</li>
                      <li>Get backup recovery codes for emergencies</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleSetupMfa}
                disabled={mfaLoading}
                className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="setup-mfa-button"
              >
                {mfaLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Setting up...
                  </>
                ) : (
                  <>
                    <MdSecurity className="w-5 h-5" />
                    Enable Two-Factor Authentication
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <MdCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-800 font-medium">MFA is enabled</p>
                  <p className="text-sm text-green-600 mt-1">
                    Your account is protected with two-factor authentication.
                    {mfaStatus.mfaVerifiedAt && (
                      <span className="block text-xs text-green-500 mt-1">
                        Enabled on {new Date(mfaStatus.mfaVerifiedAt).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleRegenerateRecoveryCodes}
                  disabled={mfaLoading}
                  className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="regenerate-codes-button"
                >
                  <MdKey className="w-4 h-4" />
                  Regenerate Recovery Codes
                </button>
                <button
                  onClick={() => setShowMfaDisableDialog(true)}
                  className="flex-1 py-2 px-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="disable-mfa-button"
                >
                  <MdClose className="w-4 h-4" />
                  Disable MFA
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Change Password Card */}
      <div className="bg-white rounded-lg shadow-card border border-gray-200 overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MdLock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 font-rubik">Change Password</h2>
              <p className="text-sm text-gray-500 font-rubik">Update your account password</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <MdCheckCircle className="w-5 h-5 text-green-500" />
              <p className="text-green-800 text-sm">{passwordSuccess}</p>
            </div>
          )}

          {passwordError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <MdWarning className="w-5 h-5 text-red-500" />
              <p className="text-red-800 text-sm">{passwordError}</p>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Current Password</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-rubik"
                placeholder="Enter current password"
                data-testid="current-password-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-rubik"
                placeholder="Enter new password"
                data-testid="new-password-input"
                required
              />
              <p className="text-xs text-gray-500 mt-1 font-rubik">Minimum 6 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-rubik"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={passwordSaving}
              className="w-full py-3 px-4 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="change-password-button"
            >
              {passwordSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Changing Password...
                </>
              ) : (
                <>
                  <MdLock className="w-5 h-5" />
                  Change Password
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Reset Poll Profile Card */}
      <div className="bg-white rounded-lg shadow-card border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <MdRefresh className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 font-rubik">Reset Poll Profile</h2>
              <p className="text-sm text-gray-500 font-rubik">Start fresh with your poll responses</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Current Stats */}
          {resetStatus && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 font-rubik">System Poll Responses</p>
                <p className="text-2xl font-bold text-gray-800">{resetStatus.systemPollResponses}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 font-rubik">Custom Poll Responses</p>
                <p className="text-2xl font-bold text-gray-800">{resetStatus.customPollResponses}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 font-rubik">Current Poll Points</p>
                <p className="text-2xl font-bold text-primary">{resetStatus.currentPoints}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 font-rubik">Last Reset</p>
                <p className="text-sm font-medium text-gray-800">{formatDate(resetStatus.lastReset)}</p>
              </div>
            </div>
          )}

          {/* Warning Box */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <MdWarning className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-2">Warning: This action has the following effects:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>You will be able to re-vote on <strong>all polls</strong> (system and custom)</li>
                  <li>Your <strong>Poll Points will be reset to 0</strong></li>
                  <li>This action <strong>cannot be undone</strong></li>
                  <li>You can only reset <strong>once per week</strong></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Schema Migration Notice */}
          {resetStatus?.schemaMigrationNeeded && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <MdInfo className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">Setup Required</p>
                <p className="mt-1">
                  The database needs to be updated to enable this feature. Please contact your administrator 
                  to run the schema migration: <code className="bg-blue-100 px-1 rounded">/app/docs/reset_poll_profile_schema.sql</code>
                </p>
              </div>
            </div>
          )}

          {/* Reset Button or Cooldown Notice */}
          {resetStatus?.canReset ? (
            <button
              onClick={() => setShowConfirmDialog(true)}
              className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="reset-poll-profile-button"
            >
              <MdRefresh className="w-5 h-5" />
              Reset My Poll Profile
            </button>
          ) : resetStatus?.schemaMigrationNeeded ? (
            <button
              disabled
              className="w-full py-3 px-4 bg-gray-300 text-gray-500 font-semibold rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
            >
              <MdRefresh className="w-5 h-5" />
              Feature Not Available Yet
            </button>
          ) : (
            <div className="p-4 bg-gray-100 rounded-lg flex items-center gap-3">
              <MdAccessTime className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-gray-700 font-medium">Reset not available yet</p>
                <p className="text-sm text-gray-500">
                  You can reset again in {resetStatus?.daysUntilReset} day(s) 
                  ({formatDate(resetStatus?.nextResetAvailable)})
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Card */}
      <div className="mt-6 bg-white rounded-lg shadow-card border border-red-200 overflow-hidden">
        <div className="p-6 border-b border-red-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <MdDeleteForever className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 font-rubik">Delete Account</h2>
              <p className="text-sm text-gray-500 font-rubik">Permanently delete your account</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Warning Box */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <MdWarning className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 font-semibold">
                Warning: This action is permanent and cannot be undone.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowDeleteDialog(true)}
            className="w-full py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="delete-account-button"
          >
            <MdDeleteForever className="w-5 h-5" />
            Delete My Account
          </button>
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <MdWarning className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Confirm Reset</h3>
            </div>

            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <p className="font-semibold">This will:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Allow you to re-vote on all {(resetStatus?.systemPollResponses || 0) + (resetStatus?.customPollResponses || 0)} polls</li>
                <li>Reset your {resetStatus?.currentPoints || 0} Poll Points to 0</li>
              </ul>
            </div>

            <p className="text-gray-600 mb-4">
              Type <strong className="text-red-600">RESET</strong> to confirm this action:
            </p>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="Type RESET"
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 font-mono text-center text-lg"
              data-testid="reset-confirm-input"
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmText('');
                  setError(null);
                }}
                className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                disabled={resetting}
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={confirmText !== 'RESET' || resetting}
                className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-reset-button"
              >
                {resetting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Resetting...
                  </>
                ) : (
                  'Confirm Reset'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA Setup Dialog */}
      {showMfaSetupDialog && mfaSetupData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 my-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-full">
                <MdSecurity className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Setup Two-Factor Authentication</h3>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p className="font-semibold mb-2">Step 1: Scan QR Code</p>
                <p>Open Google Authenticator and scan this QR code:</p>
              </div>
              
              <div className="flex justify-center p-4 bg-white border rounded-lg">
                <img 
                  src={mfaSetupData.qrCodeBase64} 
                  alt="MFA QR Code" 
                  className="w-48 h-48"
                  data-testid="mfa-qr-code"
                />
              </div>

              <div className="text-xs text-gray-500 text-center">
                <p>Cannot scan? Enter this code manually:</p>
                <code className="block mt-1 bg-gray-100 p-2 rounded font-mono text-sm break-all">
                  {mfaSetupData.secret}
                </code>
              </div>

              <div className="text-sm text-gray-600">
                <p className="font-semibold mb-2">Step 2: Verify Setup</p>
                <p>Enter the 6-digit code from your authenticator app:</p>
              </div>

              <input
                type="text"
                value={mfaVerifyCode}
                onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full p-3 border border-gray-300 rounded-lg text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                data-testid="mfa-verify-code-input"
              />

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMfaSetupDialog(false);
                    setMfaSetupData(null);
                    setMfaVerifyCode('');
                    setError(null);
                  }}
                  className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={mfaLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyMfaSetup}
                  disabled={mfaVerifyCode.length !== 6 || mfaLoading}
                  className="flex-1 py-2 px-4 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="verify-mfa-button"
                >
                  {mfaLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Verifying...
                    </>
                  ) : (
                    'Enable MFA'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Codes Dialog */}
      {showRecoveryCodesDialog && recoveryCodes.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <MdKey className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Your Recovery Codes</h3>
            </div>

            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-semibold">Important: Save these codes!</p>
              <p className="mt-1">If you lose access to your authenticator app, you will need one of these codes to sign in. Each code can only be used once.</p>
            </div>

            <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg mb-4">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="font-mono text-sm bg-white p-2 rounded border text-center">
                  {code}
                </div>
              ))}
            </div>

            <button
              onClick={copyRecoveryCodes}
              className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 mb-4"
              data-testid="copy-recovery-codes"
            >
              <MdCopyAll className="w-4 h-4" />
              Copy All Codes
            </button>

            <button
              onClick={() => {
                setShowRecoveryCodesDialog(false);
                setRecoveryCodes([]);
              }}
              className="w-full py-2 px-4 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
              data-testid="close-recovery-codes"
            >
              I Have Saved My Codes
            </button>
          </div>
        </div>
      )}

      {/* MFA Disable Dialog */}
      {showMfaDisableDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <MdWarning className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Disable Two-Factor Authentication</h3>
            </div>

            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <p className="font-semibold">Warning: This will make your account less secure!</p>
              <p className="mt-1">Disabling MFA removes the extra layer of protection on your account.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={mfaDisablePassword}
                  onChange={(e) => setMfaDisablePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  data-testid="mfa-disable-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Authenticator Code</label>
                <input
                  type="text"
                  value={mfaDisableCode}
                  onChange={(e) => setMfaDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full p-3 border border-gray-300 rounded-lg text-center text-xl tracking-widest font-mono"
                  maxLength={6}
                  data-testid="mfa-disable-code"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMfaDisableDialog(false);
                    setMfaDisableCode('');
                    setMfaDisablePassword('');
                    setError(null);
                  }}
                  className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={mfaLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisableMfa}
                  disabled={mfaDisableCode.length !== 6 || !mfaDisablePassword || mfaLoading}
                  className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="confirm-disable-mfa"
                >
                  {mfaLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Disabling...
                    </>
                  ) : (
                    'Disable MFA'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <MdDeleteForever className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Delete Account</h3>
            </div>

            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <p className="font-semibold">⚠️ This action is permanent!</p>
              <p className="mt-1">Your account and all associated data will be permanently deleted. This cannot be undone.</p>
            </div>

            {/* Optional Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Why are you leaving? (optional)
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Help us improve by sharing your reason..."
                className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none"
                rows={3}
                data-testid="delete-reason-input"
              />
            </div>

            <p className="text-gray-600 mb-4">
              Type <strong className="text-red-600">DELETE</strong> to confirm:
            </p>

            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="Type DELETE"
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 font-mono text-center text-lg"
              data-testid="delete-confirm-input"
            />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                  setDeleteReason('');
                  setError(null);
                }}
                className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-delete-button"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Toggle Switch Component for Notifications
function NotificationToggle({ label, description, enabled, onChange, disabled }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${disabled ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-gray-300'} transition-colors`}>
      <div className="flex-1 mr-4">
        <p className={`font-medium ${disabled ? 'text-gray-400' : 'text-gray-800'} font-rubik`}>{label}</p>
        <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-500'} font-rubik`}>{description}</p>
      </div>
      <button
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          disabled 
            ? 'bg-gray-200 cursor-not-allowed' 
            : enabled 
              ? 'bg-primary' 
              : 'bg-gray-300'
        }`}
        data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export default AccountSettingsPage;
